// Client\client-vite\src\components\Navbars\MainNavbar.tsx
import * as React from "react"
import { motion, useScroll, useMotionValueEvent } from "framer-motion"
import { Link } from "react-router-dom"
import {
    Menu,
    X,
    Home,
    Car,
    User2,
    Megaphone,
    Store,
    Cable,
    LineChart,
    Briefcase,
    Banknote,
    Newspaper,
    HandMetal,
    UserCircle,
    BuildingIcon,
} from "lucide-react"

import PublicNavbar from "./PublicNavbar"
import { Button } from "@/components/ui/button"
import { ThemeModeToggle } from "../Toggles/ThemeModeToggle"
import { cn } from "@/lib/utils"

export const MainNavbar = () => {
    const [isMenuOpen, setIsMenuOpen] = React.useState(false)
    const [shrink, setShrink] = React.useState(false)
    const { scrollY } = useScroll()

    useMotionValueEvent(scrollY, "change", (latest) => {
        setShrink(latest > 50)
    })

    const mobileLinks = [
        { path: "/", label: "Home", icon: Home },
        { path: "/dealer", label: "Dealers", icon: Car },
        { path: "/customers", label: "Customers", icon: User2 },
        { path: "/sales", label: "Sales", icon: Megaphone },
        { path: "/marketplaces", label: "Marketplaces", icon: Store },
        { path: "/integrations", label: "Integrations", icon: Cable },
        { path: "/insurance", label: "Insurance", icon: LineChart },
        { path: "/warranty", label: "Warranty", icon: Briefcase },
        { path: "/lenders", label: "Lenders", icon: Briefcase },
        { path: "/finance", label: "Finance", icon: Banknote },
        { path: "/news", label: "News", icon: Newspaper },
        { path: "/about", label: "About Us", icon: HandMetal },
        { path: "/mission", label: "Mission", icon: BuildingIcon },
        { path: "/careers", label: "Careers", icon: UserCircle },
        { path: "/contact", label: "Contact", icon: HandMetal },
    ]

    return (
        <motion.header
            initial={false}
            animate={{
                height: shrink ? 56 : 72,
                boxShadow: shrink
                    ? "0 2px 8px rgba(0,0,0,0.08)"
                    : "0 0 0 rgba(0,0,0,0)",
            }}
            transition={{ duration: 0.2 }}
            className={cn(
                "fixed top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
            )}
        >
            <div className="relative flex w-full items-center justify-between">
                {/* ===== Desktop View ===== */}
                <div className="hidden lg:block w-full">
                    <PublicNavbar />
                </div>

                {/* ===== Mobile Header ===== */}
                <div className="flex lg:hidden items-center justify-between w-full px-4 py-3">
                    <Link to="/" className="font-bold text-lg tracking-tight">
                        {/* IDScrm */}
                        <img src="./images/logo.png" />
                    </Link>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                        >
                            {isMenuOpen ? (
                                <X className="h-5 w-5" />
                            ) : (
                                <Menu className="h-5 w-5" />
                            )}
                        </Button>
                    </div>
                </div>

                {/* ===== Always-visible Theme Toggle ===== */}
                <div className="absolute inset-y-0 right-12 flex items-center">
                    <ThemeModeToggle />
                </div>

            </div>

            {/* ===== Mobile Drawer ===== */}
            <motion.div
                initial={false}
                animate={{
                    height: isMenuOpen ? "auto" : 0,
                    opacity: isMenuOpen ? 1 : 0,
                }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden border-t border-border bg-background md:hidden"
            >
                <div className="space-y-1 px-4 py-4">
                    {mobileLinks.map((link) => (
                        <Link
                            key={link.path}
                            to={link.path}
                            onClick={() => setIsMenuOpen(false)}
                            className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-primary transition-colors"
                        >
                            <link.icon className="h-4 w-4" />
                            {link.label}
                        </Link>
                    ))}
                </div>
            </motion.div>

        </motion.header>
    )
}

export default MainNavbar
